export type BitjitaEndpointKey =
  | 'items'
  | 'resources'
  | 'buildings'
  | 'creatures'
  | 'claims'
  | 'market'
  | 'cargo'
  | 'empires'
  | 'players';

export interface BitjitaEndpoint {
  key: BitjitaEndpointKey;
  path: string;
  label: string;
  description: string;
}

export interface PayloadSummary {
  key: BitjitaEndpointKey;
  count: number;
}

export const BITJITA_BASE_URL = 'https://bitjita.com';

export const BITJITA_ENDPOINTS: BitjitaEndpoint[] = [
  { key: 'items', path: '/api/items', label: 'Items', description: 'Compendium item records, tiers, tags, rarity, volume, and descriptions.' },
  { key: 'resources', path: '/api/resources', label: 'Resources', description: 'Gatherable resource records used by the world and compendium.' },
  { key: 'buildings', path: '/api/buildings', label: 'Buildings', description: 'Building definitions, functions, crafting/refining/storage slots, and requirements.' },
  { key: 'creatures', path: '/api/creatures', label: 'Creatures', description: 'Creature and enemy records, tiers, combat stats, and tags.' },
  { key: 'claims', path: '/api/claims', label: 'Claims', description: 'Live claim, settlement, and territory metadata.' },
  { key: 'market', path: '/api/market', label: 'Market', description: 'Trade listings and market intelligence from Bitjita.' },
  { key: 'cargo', path: '/api/cargo', label: 'Cargo', description: 'Cargo database entries and transportable goods.' },
  { key: 'empires', path: '/api/empires', label: 'Empires', description: 'Faction and empire records.' },
  { key: 'players', path: '/api/players', label: 'Players', description: 'Public player lookup and related world data.' },
];

export function buildBitjitaUrl(key: BitjitaEndpointKey, params: Record<string, string> = {}): URL {
  const endpoint = BITJITA_ENDPOINTS.find((candidate) => candidate.key === key);
  if (!endpoint) {
    throw new Error(`Unknown Bitjita endpoint: ${key}`);
  }

  const url = new URL(endpoint.path, BITJITA_BASE_URL);
  Object.entries(params).forEach(([paramKey, value]) => {
    if (value.trim().length > 0) {
      url.searchParams.set(paramKey, value);
    }
  });
  return url;
}

export function summarizeBitjitaPayload(key: BitjitaEndpointKey, payload: unknown): PayloadSummary {
  if (Array.isArray(payload)) {
    return { key, count: payload.length };
  }

  if (payload && typeof payload === 'object') {
    const keyedPayload = payload as Partial<Record<BitjitaEndpointKey, unknown>>;
    const candidate = keyedPayload[key];
    if (Array.isArray(candidate)) {
      return { key, count: candidate.length };
    }
  }

  return { key, count: 0 };
}

export async function fetchBitjitaSummary(key: BitjitaEndpointKey, signal?: AbortSignal): Promise<PayloadSummary> {
  const response = await fetch(buildBitjitaUrl(key), {
    signal,
    headers: {
      accept: 'application/json,text/plain,*/*',
    },
  });

  if (!response.ok) {
    throw new Error(`Bitjita ${key} request failed with ${response.status}`);
  }

  return summarizeBitjitaPayload(key, await response.json());
}
